import * as objectUtils from './object-utils';
import {
  AmplifyErrorSources,
  CaseArtifactTypes,
  EditorIssueLevel,
  EditorIssueProvenance,
  IssueTypeErrors
} from '../enums';
import * as vitalware from './vitalware';
import { isString, isEmpty, groupBy } from './object-utils';

// CMxCommonApp.EditorIssue
// {
//   "policy": "C-015-929",
//   "artifactType": "ASSEMBLED",
//   "source": "CCI",
//   "level": "ERROR",
//   "message": "Per policy C-015-929, R51.9 does not support medical necessity."
// }

export type CodeIssueMap = {
  [key: string]: CMxCommonApp.EditorIssue[];
};

export type CodeIssues = Record<
  'cci' | 'claims' | 'custom_rules',
  CodeIssueMap[]
>;
export type AmplifyErrors = {
  errors: AmplifyAPI.AmplifyError[];
};

export const normalzeCCIIssue = (
  cci: CMxAPI.CCIEditorIssue
): CMxCommonApp.EditorIssue => {
  const data = {
    message: cci.message,
    source: EditorIssueProvenance.CCI,
    level:
      cci.status === '2' ? EditorIssueLevel.WARNING : EditorIssueLevel.ERROR,
    artifactType: [CaseArtifactTypes.ASSEMBLED, CaseArtifactTypes.PROCESSED]
  };
  return data;
};

export const normalizeClaimsIssue = (
  claimsIssue: CMxAPI.ClaimsEditorIssue
): CMxCommonApp.EditorIssue => {
  const data = {
    message: claimsIssue.message,
    source: EditorIssueProvenance.CLAIMS,
    level:
      claimsIssue.status?.toLowerCase() === 'error'
        ? EditorIssueLevel.ERROR
        : EditorIssueLevel.WARNING,
    artifactType: [CaseArtifactTypes.ASSEMBLED, CaseArtifactTypes.PROCESSED]
  };
  return data;
};

export const normalizeCustomRuleIssue = (
  claimsIssue: CMxAPI.ClaimsEditorIssue
): CMxCommonApp.EditorIssue => {
  const data = {
    message: claimsIssue.message,
    source: EditorIssueProvenance.CUSTOM_RULES,
    level:
      claimsIssue?.status?.toLowerCase() === 'error'
        ? EditorIssueLevel.ERROR
        : EditorIssueLevel.WARNING,
    artifactType: [CaseArtifactTypes.ASSEMBLED, CaseArtifactTypes.PROCESSED]
  };
  return data;
};

export const normalizeCustomRules = (cciIssues: CMxAPI.CCIEditorIssueMap) => {
  const ret: CodeIssueMap = {};
  Object.entries(cciIssues).forEach(([key, values]) => {
    if (Array.isArray(values)) {
      ret[key] = values.map(normalizeCustomRuleIssue);
    }
  });
  return ret;
};

export const normalizeCCIS = (cciIssues: CMxAPI.CCIEditorIssueMap) => {
  const ret: CodeIssueMap = {};
  Object.entries(cciIssues).forEach(([key, values]) => {
    if (Array.isArray(values)) {
      ret[key] = values.map(normalzeCCIIssue);
    }
  });
  return ret;
};

export const normalizeClaims = (cciIssues: CMxAPI.ClaimEditorIssueMap) => {
  const ret: CodeIssueMap = {};
  if (typeof cciIssues !== 'object') {
    return ret;
  }
  Object.entries(cciIssues).forEach(([key, values]) => {
    if (Array.isArray(values)) {
      ret[key] = values.map(normalizeClaimsIssue);
    } else {
      // sometimes, the value is '{"71045":"null"}'.
      // sometimes it's '{"N63.1":"Invalid Code"}'
      // we display the latter, but not the former.
      if (values !== null && values !== 'null') {
        ret[key] = [
          normalizeClaimsIssue({
            policy: 'UNKNOWN',
            status: EditorIssueLevel.UNKNOWN,
            message: values
          })
        ];
      }
    }
  });
  return ret;
};

const _merge = (src: CodeIssueMap, mix: CodeIssueMap) => {
  Object.entries(mix).forEach(([key, value]) => {
    if (Array.isArray(src[key])) {
      src[key] = [...src[key], ...value];
    } else if (Array.isArray(value)) {
      src[key] = [...value];
    }
  });
};

const toObject = (item: any) => {
  if (isString(item)) {
    return parseVWIssues(item);
  }
  return item ?? {};
};

export const parseCodeEdits = (caseRun: CMxAPI.CaseRun) => {
  const merged: CodeIssueMap = {};

  const claims_editor_issues = toObject(caseRun.claims_editor_issues);
  const claims: CodeIssueMap = normalizeClaims(claims_editor_issues);
  _merge(merged, claims);

  const cci_editor_issues = toObject(caseRun.cci_editor_issues);
  const ccis: CodeIssueMap = normalizeCCIS(cci_editor_issues);
  _merge(merged, ccis);

  return merged;
};

export const parseVWIssues = (item: any) => {
  if (item === null || item === undefined) {
    return {};
  }
  // nested - double stringified JSON structure
  if (objectUtils.isStringifiedJSON(item)) {
    const obj = objectUtils.getValueForString(item);
    Object.entries(obj).forEach(entry => {
      const [key, value] = entry;
      if (objectUtils.isStringifiedJSON(value)) {
        obj[key] = objectUtils.getValueForString(value);
      }
    });
    return obj;
  }
  return item;
};

export const parseVWByStatus = (codeIssue: Array<any>) => {
  return codeIssue
    ?.filter(error => !isEmpty(error))
    .reduce((acc, curr) => {
      const code = Object.keys(curr)[0];

      // group issues by level
      const grouped = groupBy(curr[code], "level");
      const existingItemIndex = acc.findIndex(item => code in item);

      if (existingItemIndex !== -1) {
        Object.keys(grouped).forEach(level => {
          if (!acc[existingItemIndex][code]) {
            acc[existingItemIndex][code] = {};
          }
          if (!acc[existingItemIndex][code][level]) {
            acc[existingItemIndex][code][level] = [];
          }
          acc[existingItemIndex][code][level].push(...grouped[level]);
        });
      } else {
        acc.push({ [code]: grouped });
      }

      return acc;
    }, []);
};


/**
 * Given an Amplify Process Instance return a list of Merge History warnings.
 * @param amplifyErrors {AmplifyErrors}
 * @returns {AmplifyAPI.AmplifyError[]}
 */
export const filterMergeHistoryWarnings = (amplifyErrors: AmplifyErrors) => {
  if (!amplifyErrors || !amplifyErrors.hasOwnProperty('errors')) {
    return [];
  }

  return amplifyErrors.errors.filter(
    error => error.source === AmplifyErrorSources.MERGE_SPLIT_HISTORY
  );
};

/**
 * Given an Amplify Process Instance return a list of standard errors.
 * Exclude errors from CCI and Claims.
 * @param amplifyErrors {AmplifyErrors}
 * @returns {AmplifyAPI.AmplifyError[]}
 */
export const filterInvoiceIssues = (amplifyErrors: AmplifyErrors) => {
  if (!amplifyErrors || !amplifyErrors.hasOwnProperty('errors')) {
    return [];
  }

  return amplifyErrors.errors.filter(
    error =>
      error.source === AmplifyErrorSources.CUSTOM ||
      error.source === AmplifyErrorSources.STANDARD
  );
};

/**
 * Given an Amplify Process Instance return a map of CCI and Claims Issues
 * @param amplifyErrors {AmplifyErrors}
 * @returns {CodeIssues}
 */
export const parseInvoiceIssues = (amplifyErrors: AmplifyErrors) => {
  if (!amplifyErrors) {
    return {
      cci: [],
      claims: [],
      custom_rules: [],
      unresolved_documents: [],
      hold_issue: []
    };
  }

  const formatedV2Errors =
    vitalware.parseAndFilterV2Errors(amplifyErrors?.errors) ?? [];
  const errorList = [...amplifyErrors?.errors, ...formatedV2Errors];

  const parseAndNormalize = editError =>
    normalizeClaims(parseVWIssues(editError.message));
  const parseAndNormalizeCCI = editError =>
    normalizeCCIS(parseVWIssues(editError.message));
  const parseAndNormalizeCustomRules = editError =>
    normalizeCustomRules(parseVWIssues(editError.message));

  const claims: CodeIssueMap[] = errorList
    .filter(error => error.message_key === IssueTypeErrors.CLAIMS_EDIT)
    .map(parseAndNormalize)
    .filter(rule => Object.keys(rule).length > 0);

  const cci: CodeIssueMap[] = errorList
    .filter(error => error.message_key === IssueTypeErrors.CCI_EDIT)
    .map(parseAndNormalizeCCI)
    .filter(rule => Object.keys(rule).length > 0);

  const custom_rules: CodeIssueMap[] = errorList
    .filter(error => error.message_key === IssueTypeErrors.CUSTOM_RULES)
    .map(parseAndNormalizeCustomRules)
    .filter(rule => Object.keys(rule).length > 0);

  const unresolved_documents = errorList.filter(
    error => error.source === AmplifyErrorSources.UNRESOLVED_DOCUMENT
  );

  const hold_issue = errorList.filter(
    error => error.source === AmplifyErrorSources.HOLD
  );

  return { cci, claims, custom_rules, unresolved_documents, hold_issue };
};

/**
 * Converts a list of issues, both CCI and Claims, to a hash-map based
 * object so that the issues can be appended to codes.  The return data includes
 * claims and CCI issues so that both may be available for the same code.
 * @param codeIssues
 * @returns
 */
export const convertIssuesToCodeMap = (codeIssues: CodeIssues) => {
  const returnData = {};

  const populateIssueList = issueList => {
    Object.keys(issueList).forEach(key => {
      if (!returnData[key]) {
        returnData[key] = issueList[key];
      } else {
        returnData[key] = [...returnData[key], ...issueList[key]];
      }
    });
  };

  codeIssues.claims.forEach(populateIssueList);
  codeIssues.cci.forEach(populateIssueList);
  codeIssues.custom_rules.forEach(populateIssueList);

  return returnData;
};

/**
 * Given a process instance returns a map of code errors
 * for both CCI and claims.
 * {
 *  cci : { '77067': [{...err}] },
 *  claims : {}
 * }
 * @param processInstance {AmplifyAPI.ProcessInstance}
 * @returns
 */
export const parseProcessInstance = (
  processInstance: AmplifyAPI.ProcessInstance
) => {
  const issues = parseInvoiceIssues(processInstance as AmplifyErrors);
  return convertIssuesToCodeMap(issues);
};

/**
 * Creates synthetic errors (read:Standard) for CCI and Claims.
 * This is helpful because CCI issues are attached at the code
 * level, but may need to be represented elsewhere on the charges.
 * The view code for those errors
 * @param amplifyErrors {AmplifyAPI.ProcessInstance}
 * @returns
 */
export const createSyntheticStandardErrors = (
  amplifyErrors: AmplifyAPI.ProcessInstance
) => {
  if (!amplifyErrors || !amplifyErrors.hasOwnProperty('errors')) {
    return [];
  }

  const synthesizer = (
    message_key: string,
    errorOverride: Partial<AmplifyAPI.AmplifyError>
  ) => {
    return amplifyErrors.errors
      .filter(error => error.message_key === message_key)
      .map(error => {
        return {
          ...error,
          ...errorOverride
        };
      });
  };
  const cci = synthesizer(IssueTypeErrors.CCI_EDIT, {
    message: 'There are CCI issues for these charges',
    source: AmplifyErrorSources.STANDARD
  });

  const claims = synthesizer(IssueTypeErrors.CLAIMS_EDIT, {
    message: 'There are claims issues for these charges',
    source: AmplifyErrorSources.STANDARD
  });

  const customRules = synthesizer(IssueTypeErrors.CUSTOM_RULES, {
    message: 'There are custom rule issues for these charges',
    source: AmplifyErrorSources.STANDARD
  });

  return [...cci, ...claims, ...customRules];
};

export const parseAndFilterV2Errors = (
  errors: AmplifyAPI.AmplifyError[]
): AmplifyAPI.AmplifyError[] => {
  if (!errors || errors.length === 0) {
    return [];
  }
  const v2Errors = errors?.filter(error => error?.error_version === '2');
  const v2ErrorsByCode = groupBy(v2Errors, error => {
    if (typeof error === 'object' && 'code' in error) {
      return `{"caserun_uid": "${error?.caserun_uid}", "code": "${error?.code}", 
      "source":"${error?.source}","field":"${error?.field}","message_key":"${error?.message_key}"}`;
    }
    return '';
  });

  const restructuredErrors: AmplifyAPI.AmplifyError[] = Object.entries(
    v2ErrorsByCode
  )?.map(([key, errors]) => {
    const keyJSON = JSON.parse(key);
    let parsedError: AmplifyAPI.AmplifyErrorV1;
    const messageJSON = {
      [keyJSON?.code]: (errors as any[]).map(error => {
        if (typeof error === 'object' && 'error_status' in error) {
          parsedError = {
            error_version: 'v1',
            field: keyJSON?.field,
            message_key: keyJSON?.message_key,
            source: keyJSON?.source,
            message: '',
            formattedInUI: true
          };
          return {
            status: error?.error_status,
            message: error?.message
          };
        }
      })
    };
    parsedError.message = JSON.stringify(messageJSON);
    return parsedError;
  });
  return restructuredErrors ?? [];
};
